import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { theme, responsiveContainer, createGradient } from '../styles/theme';

function Pricing() {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const navigate = useNavigate();
  const { user } = useAuth();

  const plans = {
    monthly: [
      {
        name: 'Free',
        price: 0,
        popular: false,
        description: 'Perfect for testing and small websites',
        features: [
          '50 conversations/month',
          '1 chatbot',
          'Basic widget customization',
          'Email support',
          'ByteBot branding'
        ],
        cta: 'Get Started Free'
      },
      {
        name: 'Premium',
        price: 29,
        popular: true,
        description: 'Ideal for growing businesses',
        features: [
          'Everything in Free, plus:',
          '5,000 conversations/month',
          'Remove ByteBot branding',
          'Advanced widget customization',
          'Email support'
        ],
        cta: 'Upgrade to Premium'
      },
      {
        name: 'Growth',
        price: 79,
        popular: false,
        description: 'For businesses with high traffic',
        features: [
          'Everything in Premium, plus:',
          '15,000 conversations/month',
          '5 chatbots',
          'Priority email support'
        ],
        cta: 'Upgrade to Growth'
      },
      {
        name: 'Agency',
        price: 199,
        popular: false,
        description: 'For agencies managing multiple clients',
        features: [
          'Everything in Growth, plus:',
          '50,000 conversations/month',
          'Unlimited chatbots',
          'Dedicated email support',
          'Guaranteed response times'
        ],
        cta: 'Contact Sales'
      }
    ],
    yearly: [
      {
        name: 'Free',
        price: 0,
        popular: false,
        description: 'Perfect for testing and small websites',
        features: [
          '50 conversations/month',
          '1 chatbot',
          'Basic widget customization',
          'Email support',
          'ByteBot branding'
        ],
        cta: 'Get Started Free'
      },
      {
        name: 'Premium',
        price: 290, // 29 * 10 (2 months free)
        originalPrice: 348,
        popular: true,
        description: 'Ideal for growing businesses',
        features: [
          'Everything in Free, plus:',
          '5,000 conversations/month',
          'Remove ByteBot branding',
          'Advanced widget customization',
          'Email support'
        ],
        cta: 'Upgrade to Premium'
      },
      {
        name: 'Growth',
        price: 790, // 79 * 10 (2 months free)
        originalPrice: 948,
        popular: false,
        description: 'For businesses with high traffic',
        features: [
          'Everything in Premium, plus:',
          '15,000 conversations/month',
          '5 chatbots',
          'Priority email support'
        ],
        cta: 'Upgrade to Growth'
      },
      {
        name: 'Agency',
        price: 1990, // 199 * 10 (2 months free)
        originalPrice: 2388,
        popular: false,
        description: 'For agencies managing multiple clients',
        features: [
          'Everything in Growth, plus:',
          '50,000 conversations/month',
          'Unlimited chatbots',
          'Dedicated email support',
          'Guaranteed response times'
        ],
        cta: 'Contact Sales'
      }
    ]
  };

  const styles = {
    container: {
      backgroundColor: theme.colors.background,
      fontFamily: "'Inter', sans-serif",
      minHeight: '100vh',
    },
    
    // Enhanced Hero Section
    hero: {
      background: createGradient(theme.colors.primary, theme.colors.primaryDark, '135deg').background,
      color: '#fff',
      padding: '120px 20px 80px 20px',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
    },
    
    heroPattern: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0.1,
      background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Crect x='11' y='11' width='4' height='4'/%3E%3Crect x='51' y='11' width='4' height='4'/%3E%3Crect x='11' y='51' width='4' height='4'/%3E%3Crect x='51' y='51' width='4' height='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    },
    
    heroContent: {
      ...responsiveContainer,
      position: 'relative',
      zIndex: 1,
    },
    
    heroTitle: {
      fontSize: theme.typography.fontSizes['6xl'],
      fontWeight: theme.typography.fontWeights.extrabold,
      marginBottom: theme.spacing.lg,
      lineHeight: '1.1',
      textShadow: '0 2px 4px rgba(0,0,0,0.1)',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    heroSubtitle: {
      fontSize: theme.typography.fontSizes['2xl'],
      fontWeight: theme.typography.fontWeights.normal,
      marginBottom: theme.spacing['3xl'],
      maxWidth: '800px',
      margin: `0 auto ${theme.spacing['3xl']} auto`,
      lineHeight: '1.6',
      opacity: 0.95,
    },

    // Billing Toggle Section
    billingSection: {
      backgroundColor: theme.colors.surface,
      padding: '60px 20px',
      textAlign: 'center',
    },
    
    billingToggle: {
      display: 'inline-flex',
      backgroundColor: theme.colors.background,
      border: `2px solid ${theme.colors.borderDark}`,
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing.xs,
      position: 'relative',
    },
    
    billingOption: {
      padding: `${theme.spacing.md} ${theme.spacing.xl}`,
      fontSize: theme.typography.fontSizes.base,
      fontWeight: theme.typography.fontWeights.semibold,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      borderRadius: theme.borderRadius.md,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      position: 'relative',
      zIndex: 1,
    },
    
    billingOptionActive: {
      backgroundColor: theme.colors.primary,
      color: '#fff',
    },
    
    billingOptionInactive: {
      color: theme.colors.textLight,
    },
    
    savingsBadge: {
      position: 'absolute',
      top: '-12px',
      right: '-12px',
      backgroundColor: theme.colors.primary,
      color: '#fff',
      padding: '4px 8px',
      borderRadius: theme.borderRadius.sm,
      fontSize: theme.typography.fontSizes.xs,
      fontWeight: theme.typography.fontWeights.bold,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
    },

    // Main Pricing Section - Match Homepage exactly
    pricing: {
      backgroundColor: theme.colors.background,
      padding: '100px 20px',
    },
    
    pricingContent: {
      ...responsiveContainer,
    },
    
    pricingGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: theme.spacing.lg,
      marginTop: theme.spacing['2xl'],
    },
    
    pricingCard: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing['2xl'],
      textAlign: 'center',
      position: 'relative',
      border: `2px solid ${theme.colors.borderDark}`,
      transition: 'all 0.3s ease',
    },
    
    popularCard: {
      border: `2px solid ${theme.colors.primary}`,
      transform: 'scale(1.05)',
      backgroundColor: theme.colors.surface,
    },
    
    popularBadge: {
      position: 'absolute',
      top: '-15px',
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: theme.colors.primary,
      color: '#fff',
      padding: '8px 24px',
      borderRadius: theme.borderRadius.md,
      fontSize: theme.typography.fontSizes.sm,
      fontWeight: theme.typography.fontWeights.bold,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      border: `2px solid ${theme.colors.primary}`,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    planName: {
      fontSize: theme.typography.fontSizes['2xl'],
      fontWeight: theme.typography.fontWeights.bold,
      color: theme.colors.text,
      marginBottom: theme.spacing.md,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    planDescription: {
      fontSize: theme.typography.fontSizes.sm,
      color: theme.colors.textLight,
      marginBottom: theme.spacing.lg,
      lineHeight: '1.5',
    },
    
    planPrice: {
      fontSize: theme.typography.fontSizes['4xl'],
      fontWeight: theme.typography.fontWeights.extrabold,
      color: theme.colors.primary,
      marginBottom: theme.spacing.lg,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    originalPrice: {
      fontSize: theme.typography.fontSizes.lg,
      color: theme.colors.textLight,
      textDecoration: 'line-through',
      marginLeft: theme.spacing.sm,
    },
    
    savings: {
      fontSize: theme.typography.fontSizes.sm,
      color: theme.colors.primary,
      fontWeight: theme.typography.fontWeights.bold,
      marginTop: theme.spacing.xs,
    },
    
    planFeatures: {
      listStyle: 'none',
      padding: 0,
      marginBottom: theme.spacing.xl,
    },
    
    planFeature: {
      padding: '12px 0',
      color: theme.colors.textLight,
      borderBottom: `1px solid ${theme.colors.border}`,
      fontSize: theme.typography.fontSizes.base,
      textAlign: 'left',
    },
    
    planButton: {
      width: '100%',
      backgroundColor: theme.colors.primary,
      color: '#fff',
      border: `2px solid ${theme.colors.primary}`,
      borderRadius: theme.borderRadius.md,
      padding: '12px 16px',
      fontSize: theme.typography.fontSizes.base,
      fontWeight: theme.typography.fontWeights.bold,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },

    // FAQ Section
    faq: {
      backgroundColor: theme.colors.surface,
      padding: '100px 20px',
    },
    
    faqContent: {
      ...responsiveContainer,
      maxWidth: '800px',
    },
    
    sectionTitle: {
      fontSize: theme.typography.fontSizes['4xl'],
      fontWeight: theme.typography.fontWeights.bold,
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: theme.spacing['3xl'],
      position: 'relative',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    sectionTitleUnderline: {
      content: '""',
      position: 'absolute',
      bottom: '-10px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '80px',
      height: '4px',
      backgroundColor: theme.colors.primary,
      borderRadius: theme.borderRadius.full,
    },
    
    faqItem: {
      marginBottom: theme.spacing.xl,
      backgroundColor: theme.colors.background,
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing.xl,
      border: `2px solid ${theme.colors.borderDark}`,
    },
    
    faqQuestion: {
      fontSize: theme.typography.fontSizes.lg,
      fontWeight: theme.typography.fontWeights.bold,
      color: theme.colors.primary,
      marginBottom: theme.spacing.md,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    faqAnswer: {
      fontSize: theme.typography.fontSizes.base,
      color: theme.colors.textLight,
      lineHeight: '1.6',
    },

    // Final CTA - Match Homepage exactly
    finalCta: {
      backgroundColor: theme.colors.secondary,
      color: '#fff',
      padding: '100px 20px',
      textAlign: 'center',
      position: 'relative',
    },
    
    finalCtaContent: {
      ...responsiveContainer,
      position: 'relative',
      zIndex: 1,
    },
    
    finalCtaTitle: {
      fontSize: theme.typography.fontSizes['4xl'],
      fontWeight: theme.typography.fontWeights.bold,
      marginBottom: theme.spacing.lg,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    finalCtaText: {
      fontSize: theme.typography.fontSizes.xl,
      marginBottom: theme.spacing['2xl'],
      maxWidth: '600px',
      margin: `0 auto ${theme.spacing['2xl']} auto`,
      opacity: 0.9,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    finalCtaButton: {
      backgroundColor: theme.colors.primary,
      color: '#fff',
      border: `2px solid ${theme.colors.primary}`,
      borderRadius: theme.borderRadius.md,
      padding: '20px 50px',
      fontSize: theme.typography.fontSizes.xl,
      fontWeight: theme.typography.fontWeights.bold,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },

    // Responsive
    '@media (maxWidth: 768px)': {
      heroTitle: {
        fontSize: theme.typography.fontSizes['4xl'],
      },
      pricingGrid: {
        gridTemplateColumns: '1fr',
        gap: theme.spacing.lg,
      },
      popularCard: {
        transform: 'none',
      },
      billingToggle: {
        flexDirection: 'column',
        width: '100%',
        maxWidth: '300px',
      },
    },
  };

  const handlePlanClick = (planName) => {
    if (planName === 'Agency') {
      // Agency plan goes to contact page
      navigate('/contact');
      return;
    }

    if (planName === 'Free') {
      // Free plan - just need to sign up
      if (user) {
        // Already logged in, go to dashboard
        navigate('/dashboard');
      } else {
        // Need to sign up
        navigate('/signup');
      }
      return;
    }

    // Paid plans (Premium, Growth) - need auth then Stripe
    if (user) {
      // Already logged in, go directly to subscription page with selected plan
      navigate(`/dashboard/subscription?plan=${planName.toLowerCase()}`);
    } else {
      // Need to authenticate first, then will redirect to billing
      navigate(`/signup?plan=${planName.toLowerCase()}`);
    }
  };

  const faqData = [
    {
      question: "Can I change plans at any time?",
      answer: "Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate any billing differences."
    },
    {
      question: "What happens if I exceed my conversation limit?",
      answer: "You can purchase additional conversation blocks at a reasonable rate. We'll notify you before you reach your limit so you can decide how to proceed."
    },
    {
      question: "Can I use my own domain with the chatbot?",
      answer: "Yes, all plans allow you to embed the chatbot on your own domain. We provide clear instructions on how to embed the widget on your website."
    },
    {
      question: "What kind of support do you provide?",
      answer: "Free and Premium plans get email support. Growth gets priority email support. Agency gets dedicated email support with guaranteed response times."
    },
    {
      question: "Is there a setup fee?",
      answer: "No setup fees, ever. What you see is what you pay. We believe in transparent, honest pricing."
    }
  ];

  return (
    <div style={styles.container}>
      {/* Enhanced Hero Section */}
      <section style={styles.hero}>
        <div style={styles.heroPattern}></div>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>
            Simple, Honest Pricing
          </h1>
          <p style={styles.heroSubtitle}>
            No hidden fees. No surprise charges. No long-term contracts.<br/>
            Choose the plan that fits your business needs.
          </p>
        </div>
      </section>

      {/* Billing Toggle */}
      <section style={styles.billingSection}>
        <div style={styles.billingToggle}>
          <div 
            style={{
              ...styles.billingOption,
              ...(billingCycle === 'monthly' ? styles.billingOptionActive : styles.billingOptionInactive)
            }}
            onClick={() => setBillingCycle('monthly')}
          >
            [Monthly]
          </div>
          <div 
            style={{
              ...styles.billingOption,
              ...(billingCycle === 'yearly' ? styles.billingOptionActive : styles.billingOptionInactive),
              position: 'relative'
            }}
            onClick={() => setBillingCycle('yearly')}
          >
            [Yearly]
            <div style={styles.savingsBadge}>Save 17%</div>
          </div>
        </div>
      </section>

      {/* Main Pricing Section */}
      <section style={styles.pricing}>
        <div style={styles.pricingContent}>
          <h2 style={styles.sectionTitle}>
            Simple, Predictable Pricing
            <div style={styles.sectionTitleUnderline}></div>
          </h2>
          <div style={styles.pricingGrid}>
            {plans[billingCycle].map((plan, index) => (
              <div 
                key={plan.name}
                style={{
                  ...styles.pricingCard,
                  ...(plan.popular ? styles.popularCard : {})
                }}
              >
                {plan.popular && (
                  <div style={styles.popularBadge}>[Most Popular]</div>
                )}
                
                <h3 style={styles.planName}>[{plan.name}]</h3>
                <p style={styles.planDescription}>{plan.description}</p>
                
                <div style={styles.planPrice}>
                  ${billingCycle === 'yearly' && plan.price > 0 ? plan.price : plan.price}
                  <span style={{fontSize: theme.typography.fontSizes.lg, color: theme.colors.textLight}}>
                    {billingCycle === 'yearly' && plan.price > 0 ? '/year' : '/mo'}
                  </span>
                  {billingCycle === 'yearly' && plan.originalPrice && (
                    <span style={styles.originalPrice}>
                      ${plan.originalPrice}
                    </span>
                  )}
                </div>
                
                {billingCycle === 'yearly' && plan.originalPrice && (
                  <div style={styles.savings}>
                    Save ${plan.originalPrice - plan.price} per year
                  </div>
                )}

                <ul style={styles.planFeatures}>
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} style={styles.planFeature}>
                      • {feature}
                    </li>
                  ))}
                </ul>

                <button 
                  style={styles.planButton}
                  onClick={() => handlePlanClick(plan.name)}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = theme.colors.primaryDark;
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = theme.colors.primary;
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  [{plan.cta}]
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={styles.faq}>
        <div style={styles.faqContent}>
          <h2 style={styles.sectionTitle}>
            Frequently Asked Questions
            <div style={styles.sectionTitleUnderline}></div>
          </h2>
          
          {faqData.map((faq, index) => (
            <div key={index} style={styles.faqItem}>
              <h3 style={styles.faqQuestion}>[Q] {faq.question}</h3>
              <p style={styles.faqAnswer}>{faq.answer}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA - Match Homepage exactly */}
      <section style={styles.finalCta}>
        <div style={styles.finalCtaContent}>
          <h2 style={styles.finalCtaTitle}>Ready to Get Started?</h2>
          <p style={styles.finalCtaText}>
            Join businesses already using ByteBot<br/>
            to provide 24/7 customer support.
          </p>
          <button 
            style={styles.finalCtaButton}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme.colors.primaryDark;
              e.target.style.transform = 'translateY(-3px) scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = theme.colors.primary;
              e.target.style.transform = 'translateY(0) scale(1)';
            }}
            onClick={() => handlePlanClick('Free')}
          >
            [Start Free Today]
          </button>
        </div>
      </section>
    </div>
  );
}

export default Pricing; 